import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  getCalibrageWorklist, getCalibrationBrief, saveCalibrationBrief, generateCalibrationBrief, markCalibrageDone,
  type CalibrageItem, type CalibrationBriefData,
} from '../../lib/refonteApi';
import { useAuth } from '../../components/AuthProvider';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const inputCls = 'rounded-md border border-line-2 bg-panel px-2.5 py-1.5 text-[13px]';
const fmtDate = (d?: string | null) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }) : null);
const todayISO = () => new Date().toISOString().slice(0, 10);

// Trame par défaut de la visio de calibrage (si l'IA n'a pas encore préparé).
const DEFAULT_SECTIONS: { titre: string; questions: string[] }[] = [
  { titre: 'Contexte & activité', questions: ['Décris-moi ton activité en 2 minutes.', 'Qui sont tes meilleurs clients aujourd’hui ?', 'Qu’est-ce qui te différencie vraiment ?'] },
  { titre: 'Cible & personas', questions: ['Quel type d’entreprise vises-tu (taille, secteur, zone) ?', 'Quels décideurs veux-tu rencontrer (fonctions exactes) ?', 'Qui ne veux-tu SURTOUT pas (exclusions) ?'] },
  { titre: 'Produit & proposition de valeur', questions: ['Quel problème résous-tu, concrètement ?', 'Ton pitch en une phrase ?', 'Tes meilleures références / preuves ?'] },
  { titre: 'Questions de qualification (gates)', questions: ['Qu’est-ce qui rend un RDV vraiment qualifié pour toi ? (oui/non)', 'Y a-t-il un critère éliminatoire (gate dur) ?', 'Quelles questions poser pour le vérifier en appel ?'] },
  { titre: 'Objections & concurrence', questions: ['Quelles objections reviennent le plus ?', 'Quels concurrents sont cités ?', 'Comment y répond-on ?'] },
  { titre: 'Pitch & accroche', questions: ['Quelle accroche fonctionne le mieux ?', 'Quel angle d’attaque privilégier ?'] },
];

// Chargement IA « waouh » : marque animée + messages qui défilent.
const AI_MSGS = ['Analyse du site du client…', 'Compréhension de l’activité…', 'Identification de la cible…', 'Détection des objections…', 'Préparation des questions orientées…'];
const AiLoader: React.FC = () => {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI(x => (x + 1) % AI_MSGS.length), 1400); return () => clearInterval(t); }, []);
  return (
    <div className="flex flex-col items-center justify-center py-10 gap-3">
      <div className="relative w-16 h-16">
        <div className="absolute inset-0 rounded-full bg-hook-rose/20 animate-ping" />
        <div className="absolute inset-2 rounded-full bg-hook-rose/30 animate-pulse" />
        <div className="absolute inset-0 flex items-center justify-center text-[26px]">✨</div>
      </div>
      <div className="font-mono text-[11px] uppercase tracking-widest text-hook-rose">{AI_MSGS[i]}</div>
      <div className="text-[11.5px] text-faint">Notre IA prépare ton calibrage…</div>
    </div>
  );
};

// Calibrage — worklist des calibrages à faire (CSM) + support d'entretien en slides pour la visio.
const CalibrageGuidePage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const selected = params.get('c');
  const setSelected = (id: string | null) => setParams(p => { const n = new URLSearchParams(p); if (id) n.set('c', id); else n.delete('c'); return n; });
  const { profile } = useAuth();
  // Le CSM/direction ÉDITE le calibrage ; le SDR le CONSULTE (briefing pour comprendre son client).
  const canEdit = ['csm', 'admin', 'super_admin'].includes(profile?.role || '');

  const [list, setList] = useState<CalibrageItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try { setList(await getCalibrageWorklist()); } catch (e: any) { setErr(e.message); }
    setListLoading(false);
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(null), 2500); };
  const client = list.find(c => c.id === selected) || null;

  // ── Support de visio (client sélectionné) ──────────────────────────────────
  const [website, setWebsite] = useState('');
  const [sector, setSector] = useState('');
  const [visio, setVisio] = useState('');
  const [brief, setBrief] = useState<CalibrationBriefData | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [slide, setSlide] = useState(0);
  const [aiBusy, setAiBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!selected) return;
    setSlide(0); setBrief(null); setAnswers({}); setWebsite(''); setSector(''); setVisio('');
    getCalibrationBrief(selected).then(row => {
      if (row) { setWebsite(row.website || ''); setSector(row.sector || ''); setVisio(row.visio_url || ''); setBrief(row.brief); setAnswers(row.answers || {}); }
    }).catch(() => {});
  }, [selected]);

  const sections = brief?.sections?.length ? brief.sections : DEFAULT_SECTIONS;
  const slides = ['__prep__', ...sections.map(s => s.titre), '__cloture__'];
  const nbSlides = slides.length;

  // Pistes IA à afficher selon le thème de la slide (aide Vincent à développer).
  const hintsFor = (titre: string): { label: string; items: string[] }[] => {
    if (!brief) return [];
    const t = titre.toLowerCase(); const h: { label: string; items: string[] }[] = [];
    if (t.includes('cible') || t.includes('persona')) {
      if (brief.cible_probable) h.push({ label: 'Cible probable', items: [brief.cible_probable] });
      if (brief.personas?.length) h.push({ label: 'Personas', items: brief.personas });
    }
    if (t.includes('objection') || t.includes('concurrence')) {
      if (brief.objections_probables?.length) h.push({ label: 'Objections probables', items: brief.objections_probables });
      if (brief.concurrents_possibles?.length) h.push({ label: 'Concurrents possibles', items: brief.concurrents_possibles });
    }
    if (t.includes('qualif') || t.includes('gate')) {
      if (brief.gates_suggeres?.length) h.push({ label: 'Gates suggérés', items: brief.gates_suggeres.map(g => `${g.type === 'dur' ? '[DUR] ' : ''}${g.libelle}`) });
    }
    return h;
  };

  const analyse = async () => {
    if (!selected || !website.trim()) { setErr('Renseigne l’URL du site.'); return; }
    setAiBusy(true); setErr(null);
    try {
      const { brief: b, debug } = await generateCalibrationBrief({ client_name: client?.name, website: website.trim(), sector: sector.trim() });
      setBrief(b);
      await saveCalibrationBrief(selected, { website: website.trim(), sector: sector.trim() || null, brief: b as any });
      if (b?.sections?.length || b?.resume) { flash('Trame préparée par l’IA ✨'); setSlide(1); } // on avance direct au 1er thème
      else setErr('IA sans contenu — diagnostic : ' + JSON.stringify(debug));
    } catch (e: any) { setErr('IA indisponible (' + e.message + '). Tu peux dérouler la trame par défaut.'); }
    setAiBusy(false);
  };

  const save = async (extra?: Record<string, unknown>) => {
    if (!selected) return;
    setSaving(true);
    try { await saveCalibrationBrief(selected, { website: website.trim() || null, sector: sector.trim() || null, visio_url: visio.trim() || null, answers, ...extra } as any); }
    catch (e: any) { setErr(e.message); }
    setSaving(false);
  };

  const terminer = async () => {
    if (!selected) return;
    await save();
    try { await markCalibrageDone(selected, true); flash('Calibrage terminé ✓'); await loadList(); setSelected(null); }
    catch (e: any) { setErr(e.message); }
  };

  // ── Rendu ──────────────────────────────────────────────────────────────────
  if (!selected) {
    const aFaire = list.filter(c => !c.done).sort((a, b) => (a.calibrage_date || '9999').localeCompare(b.calibrage_date || '9999'));
    const aVenir = aFaire.filter(c => c.calibrage_date && c.calibrage_date >= todayISO());
    const aPlanifier = aFaire.filter(c => !c.calibrage_date);
    const enRetard = aFaire.filter(c => c.calibrage_date && c.calibrage_date < todayISO());
    const faits = list.filter(c => c.done);
    const Card: React.FC<{ c: CalibrageItem; tone?: string }> = ({ c, tone }) => (
      <button onClick={() => setSelected(c.id)} className="text-left rounded-card border border-line bg-panel p-4 hover:border-hook-rose transition-colors w-full">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[14px] font-bold truncate">{c.name}</span>
          {c.done && <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-st-ok/10 text-st-ok">✓ fait</span>}
        </div>
        <div className={`font-mono text-[10px] uppercase tracking-widest mt-1 ${tone || 'text-faint'}`}>{c.calibrage_date ? `📅 ${fmtDate(c.calibrage_date)}` : 'date à planifier'}</div>
      </button>
    );
    return (
      <div>
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
          <h1 className="text-[21px] font-bold tracking-tight">Calibrages</h1>
          <Link to="/app/onboarding" className="text-[12px] font-semibold text-dim hover:text-ink">← Onboarding</Link>
        </div>
        <p className="text-dim text-[13px] mb-4">Tes <b>prochains calibrages</b> (issus des onboardings lancés). Ouvre-en un pour dérouler le support d'entretien pendant la visio. Les calibrages faits rejoindront la fiche client.</p>
        {(err) && <div className="mb-3 text-[12px] text-st-bad bg-st-bad/10 border border-st-bad/20 rounded-lg px-3 py-2">{err}</div>}
        {listLoading ? <SkeletonRows count={6} /> : aFaire.length + faits.length === 0 ? (
          <EmptyState title="Aucun calibrage" hint="Les clients créés en onboarding apparaissent ici avec leur date de calibrage." />
        ) : (
          <div className="space-y-5">
            {enRetard.length > 0 && <section><h2 className="font-mono text-[10px] uppercase tracking-widest text-st-bad mb-2">En retard</h2><div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{enRetard.map(c => <Card key={c.id} c={c} tone="text-st-bad" />)}</div></section>}
            {aVenir.length > 0 && <section><h2 className="font-mono text-[10px] uppercase tracking-widest text-faint mb-2">À venir</h2><div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{aVenir.map(c => <Card key={c.id} c={c} tone="text-hook-rose" />)}</div></section>}
            {aPlanifier.length > 0 && <section><h2 className="font-mono text-[10px] uppercase tracking-widest text-faint mb-2">À planifier</h2><div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{aPlanifier.map(c => <Card key={c.id} c={c} />)}</div></section>}
            {faits.length > 0 && (
              <details className="group">
                <summary className="cursor-pointer list-none font-mono text-[10px] uppercase tracking-widest text-faint mb-2">Calibrages faits ({faits.length}) <span className="group-open:hidden">▾</span><span className="hidden group-open:inline">▴</span> · rejoindront la fiche client</summary>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{faits.map(c => <Card key={c.id} c={c} />)}</div>
              </details>
            )}
          </div>
        )}
      </div>
    );
  }

  // Support de visio (slides)
  const curKey = slides[slide];
  const isPrep = curKey === '__prep__';
  const isCloture = curKey === '__cloture__';
  const section = !isPrep && !isCloture ? sections.find(s => s.titre === curKey) : null;

  return (
    <div className="max-w-[900px] mx-auto">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <button onClick={() => setSelected(null)} className="text-[12px] font-semibold text-dim hover:text-ink">← Tous les calibrages</button>
        <div className="text-[13px] font-bold">{client?.name}{!canEdit && <span className="ml-2 font-mono text-[9px] uppercase tracking-widest text-faint">briefing · lecture</span>}</div>
        {canEdit
          ? <button disabled={saving} onClick={() => save().then(() => flash('Enregistré'))} className="text-[12px] font-semibold px-3 py-1.5 rounded-md border border-line-2 hover:border-ink">Enregistrer</button>
          : <span className="w-[80px]" />}
      </div>
      {err && <div className="mb-3 text-[12px] text-st-bad bg-st-bad/10 border border-st-bad/20 rounded-lg px-3 py-2">{err}</div>}
      {msg && <div className="mb-3 text-[12px] text-st-ok bg-st-ok/10 border border-st-ok/20 rounded-lg px-3 py-2">{msg}</div>}

      {/* Progress dots */}
      <div className="flex items-center justify-center gap-1.5 mb-4">
        {slides.map((_, i) => (
          <button key={i} onClick={() => setSlide(i)} className={`h-1.5 rounded-full transition-all ${i === slide ? 'w-6 bg-hook-rose' : 'w-2.5 bg-line-2 hover:bg-dim'}`} aria-label={`Étape ${i + 1}`} />
        ))}
      </div>

      {/* Enregistrement épinglé — accessible pendant la prise de notes */}
      {visio && (
        <div className="flex items-center justify-center mb-3">
          <a href={/^https?:/.test(visio) ? visio : `https://${visio}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-hook-rose hover:underline">🎧 Ré-écouter l'enregistrement de la visio ↗</a>
        </div>
      )}

      {/* Slide */}
      <div className="bg-panel border border-line rounded-card p-6 min-h-[46vh] flex flex-col">
        {isPrep ? (
          <>
            <div className="font-mono text-[10px] uppercase tracking-widest text-faint">{canEdit ? 'Préparation' : 'Le client en bref'}</div>
            <h2 className="text-[20px] font-bold mb-1">{canEdit ? 'Avant de démarrer la visio' : `Comprendre ${client?.name || 'ce client'}`}</h2>
            <p className="text-[13px] text-dim mb-4">{canEdit ? "Colle le site du client : l'IA prépare une trame de questions orientées sur SON activité (effet wow). Sinon, déroule la trame par défaut." : "Le calibrage préparé par le CSM — pour comprendre qui est le client, ce qu'il fait et qui appeler, avant de composer."}</p>
            {canEdit && (
              <div className="flex flex-wrap items-end gap-2 mb-4">
                <label className="flex-1 min-w-[220px]"><span className="font-mono text-[9px] uppercase tracking-widest text-faint">Site web</span><input value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://…" className={inputCls + ' w-full mt-0.5'} /></label>
                <label><span className="font-mono text-[9px] uppercase tracking-widest text-faint">Secteur</span><input value={sector} onChange={e => setSector(e.target.value)} className={inputCls + ' w-40 mt-0.5'} /></label>
                <button disabled={aiBusy} onClick={analyse} className="text-[13px] font-semibold px-4 py-2 rounded-md bg-hook-rose text-white disabled:opacity-40">{aiBusy ? 'Analyse…' : brief ? '↻ Ré-analyser' : '✨ Préparer avec l’IA'}</button>
              </div>
            )}
            {!canEdit && website && <a href={/^https?:/.test(website) ? website : `https://${website}`} target="_blank" rel="noreferrer" className="inline-block mb-3 text-[12px] font-semibold text-hook-rose hover:underline">🌐 {website} ↗</a>}
            {aiBusy && <AiLoader />}
            {brief?.resume && (
              <div className="rounded-lg bg-panel-2 border border-line p-3 text-[12.5px] text-dim leading-relaxed">
                <div className="font-mono text-[9px] uppercase tracking-widest text-faint mb-1">Ce que dit leur site</div>
                {brief.resume}
                {brief.cible_probable && <p className="mt-2"><span className="font-semibold text-ink">Cible probable : </span>{brief.cible_probable}</p>}
              </div>
            )}
          </>
        ) : isCloture ? (
          <>
            <div className="font-mono text-[10px] uppercase tracking-widest text-faint">{canEdit ? 'Clôture' : 'Enregistrement'}</div>
            <h2 className="text-[20px] font-bold mb-1">{canEdit ? 'Finaliser le calibrage' : 'Revoir la visio'}</h2>
            {/* Résumé du calibrage : tout ce qui a été capté, par thème */}
            {(() => {
              const filled = sections.filter(s => (answers[s.titre] || '').trim());
              return (
                <div className="mb-4 rounded-lg bg-panel-2 border border-line p-3">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-faint mb-1.5">📋 Résumé du calibrage</div>
                  {filled.length === 0 ? <p className="text-[12px] text-faint">Aucune note captée pour l'instant.</p> : (
                    <div className="space-y-2 max-h-[34vh] overflow-y-auto">{filled.map(s => (
                      <div key={s.titre}><div className="text-[12px] font-semibold">{s.titre}</div><p className="text-[12.5px] text-dim whitespace-pre-wrap leading-relaxed">{answers[s.titre]}</p></div>
                    ))}</div>
                  )}
                </div>
              );
            })()}
            {canEdit ? (<>
              <p className="text-[13px] text-dim mb-4">Colle le lien de l'enregistrement de la visio, puis marque le calibrage comme terminé.</p>
              <label className="block mb-4"><span className="font-mono text-[9px] uppercase tracking-widest text-faint">Lien de l'enregistrement (Leexi / Meet / Zoom)</span>
                <input value={visio} onChange={e => setVisio(e.target.value)} placeholder="https://…" className={inputCls + ' w-full mt-0.5'} /></label>
              <button disabled={saving} onClick={terminer} className="self-start text-[14px] font-semibold px-5 py-2.5 rounded-md bg-st-ok text-white hover:opacity-90 disabled:opacity-40">✓ Terminer le calibrage</button>
            </>) : visio ? (
              <a href={/^https?:/.test(visio) ? visio : `https://${visio}`} target="_blank" rel="noreferrer" className="inline-block text-[13px] font-semibold text-hook-rose hover:underline">▶ Revoir l'enregistrement de la visio de calibrage ↗</a>
            ) : <p className="text-[13px] text-faint">Pas d'enregistrement de visio disponible.</p>}
          </>
        ) : section ? (
          <>
            <div className="font-mono text-[10px] uppercase tracking-widest text-hook-rose">Étape {slide} / {nbSlides - 2}</div>
            <h2 className="text-[20px] font-bold mb-3">{section.titre}</h2>
            <div className="mb-3">
              <div className="font-mono text-[9px] uppercase tracking-widest text-hook-rose mb-1.5">✨ À faire parler {brief?.sections?.length ? '(par l’IA)' : ''}</div>
              <ul className="space-y-1.5">{section.questions.map((q, i) => <li key={i} className="text-[14px] flex gap-2"><span className="text-hook-rose">›</span>{q}</li>)}</ul>
            </div>
            {hintsFor(section.titre).length > 0 && (
              <div className="mb-3 rounded-lg bg-hook-rose/5 border border-hook-rose/20 p-2.5 space-y-1.5">
                {hintsFor(section.titre).map((hh, i) => (
                  <div key={i}><span className="font-mono text-[9px] uppercase tracking-widest text-faint">{hh.label} : </span><span className="text-[12px] text-dim">{hh.items.join(' · ')}</span></div>
                ))}
              </div>
            )}
            <div className="mt-auto">
              <div className="font-mono text-[9px] uppercase tracking-widest text-faint mb-1">Ce que {canEdit ? 'dit' : 'a dit'} le client</div>
              {canEdit ? (
                <textarea value={answers[section.titre] || ''} onChange={e => setAnswers(a => ({ ...a, [section.titre]: e.target.value }))} onBlur={() => save()} rows={4} className={inputCls + ' w-full'} placeholder="Notes captées pendant la visio…" />
              ) : (
                <p className="text-[13px] text-dim bg-panel-2 border border-line rounded-lg p-3 whitespace-pre-wrap min-h-[3rem]">{answers[section.titre] || '— (rien de noté sur ce point)'}</p>
              )}
            </div>
          </>
        ) : null}
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between mt-4">
        <button disabled={slide === 0} onClick={() => setSlide(s => Math.max(0, s - 1))} className="text-[13px] font-semibold px-4 py-2 rounded-md border border-line-2 hover:border-ink disabled:opacity-30">‹ Précédent</button>
        <span className="font-mono text-[11px] text-faint">{slide + 1} / {nbSlides}</span>
        <button disabled={slide === nbSlides - 1} onClick={() => { save(); setSlide(s => Math.min(nbSlides - 1, s + 1)); }} className="text-[13px] font-semibold px-4 py-2 rounded-md bg-ink text-white hover:opacity-90 disabled:opacity-30">Suivant ›</button>
      </div>
    </div>
  );
};

export default CalibrageGuidePage;
